import { clsx } from "clsx";
import React, { forwardRef } from "react";
import type { ForwardedRef } from "react";
import { ContextualHelp, Text, Icon } from "@appsmith/wds";
import { Checkbox as HeadlessCheckbox } from "react-aria-components";

import styles from "./styles.module.css";
import type { CheckboxProps } from "./types";

const _Checkbox = (
  props: CheckboxProps,
  ref: ForwardedRef<HTMLLabelElement>,
) => {
  const {
    children,
    className,
    contextualHelp,
    isIndeterminate,
    isRequired,
    labelPosition = "end",
    ...rest
  } = props;

  const checkbox = (
    <HeadlessCheckbox
      ref={ref}
      {...rest}
      className={clsx(styles.checkbox, className)}
      data-label-position={labelPosition}
      isIndeterminate={isIndeterminate}
    >
      {Boolean(isIndeterminate) ? <Icon name="minus" /> : <Icon name="check" />}
      {Boolean(children) && <Text lineClamp={1}>{children}</Text>}
      {Boolean(isRequired) && (
        <Text color="negative" data-inline-label-necessity-indicator-icon="">
          *
        </Text>
      )}
    </HeadlessCheckbox>
  );

  // The help trigger is a button, so it must stay outside the checkbox
  // <label> — otherwise pressing it would toggle the checkbox and nest
  // interactive content in a label. DOM is unchanged when the prop is absent.
  if (Boolean(contextualHelp)) {
    return (
      <div className={styles.checkboxWithHelp} data-checkbox-with-help="">
        {checkbox}
        <ContextualHelp contextualHelp={contextualHelp} />
      </div>
    );
  }

  return checkbox;
};

export const Checkbox = forwardRef(_Checkbox);
